// Dynamic Pricing Section con widget LIVE: fetch a /api/public/competitor-prices/bulk
// para 3 SKUs reales con scraping activo (cocina + heladera + TV Electrolux),
// muestra nuestro precio vs el mínimo de competencia + timestamp del último scrape.
//
// 🔴 El bloque sólo existe si llegaron datos reales. La API responde «no disponible»
// mientras no haya una empresa demo: entonces la sección queda con su texto y sin
// el bloque. Nunca se rellena con números de ejemplo presentados como vivos.

import { motion } from 'motion/react';
import { ArrowRight, CheckCircle, LineChart, AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { ShowcaseBg, MockupWrapper } from './showcase-utils';
import { useBookDemoStore } from '../store/book-demo-store';
import { API_BASE_URL } from '@/lib/utils/constants';

// Vault 346 (L0): la misma base que el resto de la app (relativa por omisión), sin la IP de la VM.
const API_BASE = API_BASE_URL;

// SKUs reales con 4 competidores cada uno (Multicenter, El Genio X, etc.)
const LIVE_SKUS = ['133286', '135442', '133609'];

interface CompetitorPrice {
  store: string;
  price: number;
  lastUpdate?: string | null;
}
interface CompPriceEntry {
  ownPrice: number | null;
  productName?: string;
  competitors: CompetitorPrice[];
}
interface BulkResponse {
  success: boolean;
  data: Record<string, CompPriceEntry>;
}

const SHORT_NAMES: Record<string, string> = {
  '133286': 'Cocina Electrolux 5H',
  '135442': 'Heladera Electrolux',
  '133609': 'TV 65" 4K UHD ThinQ',
};

function fmtBs(n: number): string {
  return new Intl.NumberFormat('es-BO', { maximumFractionDigits: 0 }).format(Math.round(n));
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'hace instantes';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return 'ahora mismo';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

interface FilaEnVivo {
  sku: string;
  name: string;
  own: number;
  minComp: number;
  diff: number;
  compCount: number;
}

/** Sólo los productos con precio propio y de competencia: lo demás no compara nada. */
function filasConDatos(data: Record<string, CompPriceEntry> | null): FilaEnVivo[] {
  return LIVE_SKUS.flatMap((sku) => {
    const entry = data?.[sku];
    const own = entry?.ownPrice ?? null;
    const comps = entry?.competitors ?? [];
    if (!own || comps.length === 0) return [];
    const minComp = Math.min(...comps.map((c) => c.price));
    return [
      {
        sku,
        name: SHORT_NAMES[sku] || sku,
        own,
        minComp,
        diff: ((minComp - own) / own) * 100,
        compCount: comps.length,
      },
    ];
  });
}

function useComparativaEnVivo() {
  const [data, setData] = useState<Record<string, CompPriceEntry> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/competitor-prices/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skus: LIVE_SKUS }),
    })
      .then((r) => (r.ok ? (r.json() as Promise<BulkResponse>) : null))
      .then((j) => {
        if (!cancelled && j?.success) setData(j.data);
      })
      .catch(() => {
        /* sin datos: la sección queda sin el bloque */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}

function DynamicPricingMockup({
  data,
  rows,
}: {
  data: Record<string, CompPriceEntry>;
  rows: FilaEnVivo[];
}) {
  // Encontrar el último scrape entre todas las competencias
  const lastScrapeIso = (() => {
    const all: string[] = [];
    Object.values(data).forEach((e) =>
      e.competitors.forEach((c) => {
        if (c.lastUpdate) all.push(c.lastUpdate);
      })
    );
    return all.sort().reverse()[0] || null;
  })();

  // Para el alert, buscar el caso donde el minComp es más bajo que el nuestro
  const alertRow = rows.find((r) => r.diff < -1);

  return (
    <>
      {/* Card 1: Live monitor */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        style={{ transform: 'translateZ(20px)' }}
      >
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/25">
              <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
              En vivo
            </span>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Monitor de Precios
            </p>
          </div>
          {lastScrapeIso && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
              {`scrape ${fmtRelative(lastScrapeIso)}`}
            </p>
          )}
        </div>
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-4 py-2 border-b border-gray-50 dark:border-gray-700/50">
          {['Producto', 'Tu Precio', 'Comp. mín.', 'Dif.'].map((h) => (
            <p
              key={h}
              className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase"
            >
              {h}
            </p>
          ))}
        </div>
        {rows.map((row) => (
          <div
            key={row.sku}
            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
          >
            <div className="min-w-0">
              <p className="text-[11px] text-gray-700 dark:text-gray-300 truncate">{row.name}</p>
              <p className="text-[9px] text-gray-400 dark:text-gray-500">
                {row.compCount} tiendas comparadas
              </p>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right font-mono">
              {`Bs ${fmtBs(row.own)}`}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-right font-mono">
              {`Bs ${fmtBs(row.minComp)}`}
            </p>
            <p
              className={`text-[11px] font-semibold text-right ${
                row.diff > 0
                  ? 'text-green-600 dark:text-green-400'
                  : row.diff < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {`${row.diff > 0 ? '+' : ''}${row.diff.toFixed(1)}%`}
            </p>
          </div>
        ))}
      </div>

      {/* Card 2: 7-day trend bars (ilustrativo) */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
        style={{ transform: 'translateZ(14px)' }}
      >
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Tendencia 7 d&iacute;as · Categoría línea blanca
        </p>
        <div className="flex items-end gap-2 h-20">
          {[72, 65, 78, 60, 85, 70, 90].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <motion.div
                className="w-full rounded-t bg-gradient-to-t from-teal-500 to-emerald-400"
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.08, ease: 'easeOut' }}
              />
              <span className="text-[9px] text-gray-400 dark:text-gray-500">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][i]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 3: Alert basada en datos reales. Sin una diferencia real, no hay alerta. */}
      {alertRow && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3"
          style={{ transform: 'translateZ(18px)' }}
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            <span className="font-semibold">Alerta:</span>{' '}
            {`competencia ${Math.abs(alertRow.diff).toFixed(1)}% más barata en ${alertRow.name}`}
          </p>
        </motion.div>
      )}
    </>
  );
}

// ── Main export ─────────────────────────────────────────────────

export function DynamicPricingSection() {
  const openDemo = useBookDemoStore((s) => s.open);
  const data = useComparativaEnVivo();
  const rows = filasConDatos(data);
  const hayDatos = data !== null && rows.length > 0;

  return (
    <section id="dynamic-pricing" className="relative py-24 sm:py-32 overflow-hidden">
      <ShowcaseBg />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={
            hayDatos ? 'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center' : 'max-w-3xl mx-auto'
          }
        >
          {/* Text */}
          <ScrollReveal direction="left" className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 shadow-sm">
              <LineChart className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
              <span className="text-sm font-medium text-teal-700 dark:text-teal-300">
                Pricing Din&aacute;mico
              </span>
            </div>

            <div className="space-y-2">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
                Tu competencia cambia precios.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                  ¿Tú te enteras?
                </span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Monitoreo automático de precios en e-commerce. Cuando un competidor cambia precios,
                el sistema te alerta y puede ajustar los tuyos para maximizar margen.
              </p>
            </div>

            <ul className={hayDatos ? 'space-y-3' : 'grid sm:grid-cols-2 gap-3'}>
              {[
                'Scraping de precios en Shopify, VTEX, Magento y más',
                'Alertas cuando un competidor cambia precios',
                'Reglas automáticas de ajuste de precios',
                'Historial de precios y tendencias del mercado',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => openDemo({ message: 'Quiero ver Pricing Dinámico en acción' })}
              className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors group"
            >
              Ver Pricing Dinámico con mis productos
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </ScrollReveal>

          {/* Mockup: sólo con datos reales */}
          {hayDatos && (
            <MockupWrapper>
              <DynamicPricingMockup data={data} rows={rows} />
            </MockupWrapper>
          )}
        </div>
      </div>
    </section>
  );
}
